import { FormikErrors } from "formik";
import {
    ScheduleAccountMode,
    isScheduleHoldingAccountType,
    isScheduleRecognitionAccountType,
} from "@common/constants/domains/accounting/scheduleAccountFilters";
import { ScheduleType } from "@common/types/domains/accounting/schedule";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { MultiEntityAccountCombobox } from "@components/accounts/MultiEntityAccountCombobox";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { CloseIcon } from "@icons";
import {
    ScheduleAccountBlockForm,
    WorkspaceAccountWithEntity,
} from "./scheduleFormHelpers";
import styles from "./ScheduleDrawer.module.scss";

interface ScheduleAccountBlockProps {
    block: ScheduleAccountBlockForm;
    mode: ScheduleAccountMode;
    accounts: WorkspaceAccountWithEntity[];
    isAccountsLoading: boolean;
    canRemove: boolean;
    blockErrors: string | FormikErrors<ScheduleAccountBlockForm> | undefined;
    shouldShowErrors: boolean;
    onChange: (patch: Partial<ScheduleAccountBlockForm>) => void;
    onRemove: () => void;
}

function getBlockFieldError(
    blockErrors: string | FormikErrors<ScheduleAccountBlockForm> | undefined,
    field: "entityIds" | "holdingAccountName" | "recognitionAccountName",
): string | undefined {
    if (blockErrors == null || typeof blockErrors === "string") {
        return undefined;
    }
    const fieldError = blockErrors[field];
    return typeof fieldError === "string" ? fieldError : undefined;
}

export const ScheduleAccountBlock: React.FC<ScheduleAccountBlockProps> = ({
    block,
    mode,
    accounts,
    isAccountsLoading,
    canRemove,
    blockErrors,
    shouldShowErrors,
    onChange,
    onRemove,
}) => {
    const holdingAccounts = accounts.filter((account) =>
        isScheduleHoldingAccountType({ accountType: account.type, mode }),
    );
    const recognitionAccounts = accounts.filter((account) =>
        isScheduleRecognitionAccountType({
            accountType: account.type,
            scheduleType: mode.type,
        }),
    );
    const entitiesError = shouldShowErrors
        ? getBlockFieldError(blockErrors, "entityIds")
        : undefined;
    const holdingError = shouldShowErrors
        ? getBlockFieldError(blockErrors, "holdingAccountName")
        : undefined;
    const recognitionError = shouldShowErrors
        ? getBlockFieldError(blockErrors, "recognitionAccountName")
        : undefined;

    return (
        <div className={styles.accountBlock}>
            <HStack gap={8} align="center" wrap>
                <span className={styles.inlineLabel}>Business</span>
                <EntityFilterCombobox
                    mode="selection"
                    onlyBusiness
                    filters={{ entityIds: block.entityIds }}
                    onChange={(patch) =>
                        onChange({ entityIds: patch.entityIds ?? [] })
                    }
                />
                {canRemove && (
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        iconOnly
                        aria-label="Remove accounts row"
                        className={styles.blockRemoveButton}
                        onClick={onRemove}
                    >
                        <CloseIcon />
                    </Button>
                )}
            </HStack>
            {entitiesError && (
                <p className={styles.fieldError}>{entitiesError}</p>
            )}
            <HStack gap={8} align="center" wrap>
                <span className={styles.inlineLabel}>Holding GL account</span>
                <MultiEntityAccountCombobox
                    selectedAccountName={block.holdingAccountName ?? undefined}
                    selectedEntityIds={block.entityIds}
                    accounts={holdingAccounts}
                    accountsLoading={isAccountsLoading}
                    coverage="all"
                    size="md"
                    onChange={(account) =>
                        onChange({ holdingAccountName: account?.name ?? null })
                    }
                />
            </HStack>
            {holdingError && (
                <p className={styles.fieldError}>{holdingError}</p>
            )}
            <HStack gap={8} align="center" wrap>
                <span className={styles.inlineLabel}>
                    {mode.type === ScheduleType.expense
                        ? "Expense GL account"
                        : "Recognition GL account"}
                </span>
                <MultiEntityAccountCombobox
                    selectedAccountName={
                        block.recognitionAccountName ?? undefined
                    }
                    selectedEntityIds={block.entityIds}
                    accounts={recognitionAccounts}
                    accountsLoading={isAccountsLoading}
                    coverage="all"
                    size="md"
                    onChange={(account) =>
                        onChange({
                            recognitionAccountName: account?.name ?? null,
                        })
                    }
                />
            </HStack>
            {recognitionError && (
                <p className={styles.fieldError}>{recognitionError}</p>
            )}
        </div>
    );
};
